import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Avatar,
  Tabs,
  Spin,
  Skeleton,
  Row,
  Col,
  Segmented,
  Space
} from 'antd';
import {
  ArrowLeftOutlined,
  CarOutlined,
  RiseOutlined,
  BankOutlined,
  ShopOutlined,
  UserOutlined,
  AppstoreOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardService from '../../service';
import LotCard from '../../components/LotCard';
import VinCopy from '../../components/VinCopy';
import CreatorInfo from '../../components/CreatorInfo';

const SubdealerDetail = () => {
  const { subdealerId, lang } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subdealer, setSubdealer] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [manager, setManager] = useState(null);
  const [purchasedCars, setPurchasedCars] = useState([]);
  const [activeTab, setActiveTab] = useState('0');
  const [winCars, setWinCars] = useState([]);
  const [bids, setBids] = useState([]);
  const [bidViewMode, setBidViewMode] = useState('grouped');
  const [groupedBids, setGroupedBids] = useState([]);
  const [isBidsGrouped, setIsBidsGrouped] = useState(false);
  const [subdealerStats, setSubdealerStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    const loadSubdealerData = async () => {
      try {
        const response = await DashboardService.getUserById(subdealerId);
        if (response.success && response.user) {
          const userData = response.user;
          if (!userData.branchName && userData.branchId) {
            try {
              const branchRes = await DashboardService.getBranchById(userData.branchId);
              if (branchRes?.success && branchRes?.branch?.name) {
                userData.branchName = branchRes.branch.name;
              }
            } catch (e) {
              // ignore branch lookup failure
            }
          }
          setSubdealer(userData);

          if (response.user.dealerId) {
            const dealerResponse = await DashboardService.getUserById(response.user.dealerId);
            if (dealerResponse.success) setDealer(dealerResponse.user);
          }

          if (response.user.managerId) {
            const managerResponse = await DashboardService.getUserById(response.user.managerId);
            if (managerResponse.success) setManager(managerResponse.user);
          }

          try {
            const purchasedLotsResponse = await DashboardService.getMyPurchasedLots({ page: 1, limit: 1000 });
            if (purchasedLotsResponse?.success && purchasedLotsResponse.lots) {
              setPurchasedCars(purchasedLotsResponse.lots);
            } else if (purchasedLotsResponse?.lots) {
              setPurchasedCars(purchasedLotsResponse.lots);
            }
          } catch (error) {
            console.error('Error loading purchased lots:', error);
          }

          loadSubdealerStats();
        }
      } catch (error) {
        console.error('Error loading subdealer data:', error);
      }
    };

    if (subdealerId) {
      loadSubdealerData();
    }
  }, [subdealerId]);

  const loadSubdealerStats = async () => {
    if (!subdealerId) return;
    try {
      setStatsLoading(true);
      const dashboardResponse = await DashboardService.getSubdealerDashboard(subdealerId);
      if (dashboardResponse.success && dashboardResponse.dashboard) {
        setSubdealerStats(dashboardResponse.dashboard.overview);
        if (dashboardResponse.dashboard.recentActivity?.allWins) {
          setWinCars(dashboardResponse.dashboard.recentActivity.allWins);
        }
      }
    } catch (error) {
      console.log('Dashboard endpoint not available, trying stats endpoint');
      try {
        const statsResponse = await DashboardService.getBidStats('user', subdealerId);
        if (statsResponse.success) {
          setSubdealerStats(statsResponse.stats);
        }
      } catch (err) {
        console.error('Error loading subdealer stats:', err);
      }
    } finally {
      setStatsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/${lang}/subdealers`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'success';
      case 'active': return 'blue';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  const loadSubdealerBids = async () => {
    if (!subdealerId) return;
    try {
      const groupByLot = bidViewMode === 'grouped';
      const response = await DashboardService.getUserBids(subdealerId, {
        page: 1,
        limit: 50,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        groupByLot
      });

      if (response.success !== false) {
        const bidsData = response.lots || response.bids || [];
        setGroupedBids(bidsData);
        setIsBidsGrouped(response.grouped || false);
      }
    } catch (error) {
      console.error('Error loading subdealer bids:', error);
      setGroupedBids([]);
    }
  };

  useEffect(() => {
    if (activeTab === '0' && subdealerId) {
      loadSubdealerBids();
    }
  }, [bidViewMode, activeTab, subdealerId]);

  useEffect(() => {
    if (activeTab === '1' && subdealerId) {
      loadWinCars();
    }
  }, [activeTab, subdealerId]);

  const loadWinCars = async () => {
    if (winCars.length > 0) return;
    if (!subdealerId) return;
    try {
      const response = await DashboardService.getUserWinningBids(subdealerId);
      if (response.success && response.winningLots) {
        setWinCars(response.winningLots);
      } else if (response.winningLots) {
        setWinCars(response.winningLots);
      }
    } catch (error) {
      console.error('Error loading winning lots:', error);
      setWinCars([]);
    }
  };

  if (!subdealer) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Flex gap={16} style={{ marginBottom: 24 }}>
          <Skeleton.Avatar active size={64} />
          <div style={{ flex: 1 }}>
            <Skeleton.Input active block style={{ marginBottom: 8 }} />
            <Skeleton.Input active block size="small" />
          </div>
        </Flex>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Card><Skeleton active /></Card>
            </Col>
          ))}
        </Row>
        <Skeleton.Button active block style={{ marginBottom: 16 }} />
        <Card><Skeleton active paragraph={{ rows: 5 }} /></Card>
      </div>
    );
  }

  const branchName = subdealer.branchName || 'Unknown Branch';

  const stats = [
    {
      title: 'Total Bids',
      value: subdealerStats?.totalBids || groupedBids.length,
      icon: <RiseOutlined />,
      color: '#2196f3',
      subtitle: subdealerStats?.activeBids ? `${subdealerStats.activeBids} active` : ''
    },
    {
      title: 'Won Auctions',
      value: subdealerStats?.wonAuctions || 0,
      icon: <CarOutlined />,
      color: '#4caf50',
      subtitle: subdealerStats?.successRate ? `${subdealerStats.successRate.toFixed(1)}% success` : ''
    },
    {
      title: 'Total Spent',
      value: subdealerStats?.totalBidAmount ? `$${parseFloat(subdealerStats.totalBidAmount).toLocaleString()}` : '$0',
      icon: <RiseOutlined />,
      color: '#ff9800',
      subtitle: subdealerStats?.avgBidAmount ? `Avg: $${parseFloat(subdealerStats.avgBidAmount).toLocaleString()}` : ''
    },
    {
      title: 'Purchased Cars',
      value: purchasedCars.length,
      icon: <CarOutlined />,
      color: '#9c27b0',
      subtitle: purchasedCars.length > 0 ? `${purchasedCars.filter(c => !c.isPaid).length} unpaid` : ''
    }
  ];

  const bidColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Bid Amount',
      key: 'bidAmount',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#1e3a5f' }}>
          ${((record.bidAmount || record.bid) || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (v) => v ? <Tag>{v}</Tag> : 'N/A'
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'time',
      render: (v) => v ? new Date(v).toLocaleString() : 'N/A'
    }
  ];

  const winCarsColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      render: (v) => v ? <Tag color="blue">{v}</Tag> : 'N/A'
    },
    {
      title: 'Your Bid',
      key: 'currentBid',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#22c55e' }}>
          ${(record.bidAmount || record.bid || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Buyer Number',
      dataIndex: 'buyerNumber',
      key: 'buyerNumber',
      render: (v) => v || 'N/A'
    },
    {
      title: 'Last Update',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v) => v ? new Date(v).toLocaleString() : 'N/A'
    },
    {
      title: 'Times Updated',
      dataIndex: 'updatedCount',
      key: 'updatedCount',
      render: (v) => (
        <Tag color={v > 5 ? 'warning' : v > 0 ? 'blue' : 'default'} title={`Bid updated ${v || 0} times`}>
          {v || 0}
        </Tag>
      )
    }
  ];

  const purchasedColumns = [
    {
      title: 'Image',
      key: 'image',
      render: (_, record) => {
        const carImage = record.images?.length > 0 ? record.images[0].url : null;
        return (
          <div style={{ width: 80, height: 60, position: 'relative' }}>
            {carImage && (
              <img
                src={carImage}
                alt={`${record.year} ${record.make} ${record.model}`}
                style={{
                  width: 80,
                  height: 60,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #d9d9d9'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            <div
              style={{
                width: 80,
                height: 60,
                display: carImage ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: 4,
                border: '1px solid #d9d9d9'
              }}
            >
              <CarOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
            </div>
          </div>
        );
      }
    },
    {
      title: 'Lot Number',
      key: 'lotNumber',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.lotNumber}</div>
          {record.gatePassPin && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>PIN: {record.gatePassPin}</Typography.Text>
          )}
        </div>
      )
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.year} {record.make} {record.model}</div>
          {record.titleStatus && <Tag>{record.titleStatus}</Tag>}
        </div>
      )
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Purchase Price',
      key: 'purchasePrice',
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>
          ${(record.purchasePriceInDollars || record.purchasePrice || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Fees',
      key: 'fees',
      render: (_, record) => (
        <span>
          ${(record.purchasePriceFeeInDollars || record.purchasePriceFee || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, record) => {
        const total = (record.purchasePriceInDollars || record.purchasePrice || 0) + (record.purchasePriceFeeInDollars || record.purchasePriceFee || 0);
        return <span style={{ fontWeight: 700, color: '#1e3a5f' }}>${total.toLocaleString()}</span>;
      }
    },
    {
      title: 'Unpaid',
      key: 'unpaid',
      render: (_, record) => {
        const unpaid = record.unpaidAmountInDollars || record.unpaidAmount || 0;
        return (
          <span style={{ fontWeight: 700, color: unpaid > 0 ? '#ff4d4f' : '#22c55e' }}>
            ${unpaid.toLocaleString()}
          </span>
        );
      }
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (v) => v || 'N/A'
    }
  ];

  const tabItems = [
    {
      key: '0',
      label: `My Bids (${subdealerStats?.totalBids || groupedBids.length})`,
      children: (
        <div>
          <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>My Bids</Typography.Title>
            <Segmented
              value={bidViewMode}
              onChange={setBidViewMode}
              options={[
                { value: 'grouped', label: <><AppstoreOutlined /> Grouped by Lot</> },
                { value: 'flat', label: <><UnorderedListOutlined /> Flat List</> }
              ]}
            />
          </Flex>

          {isBidsGrouped && bidViewMode === 'grouped' ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {groupedBids.length > 0 ? (
                groupedBids.map((lot, index) => (
                  <LotCard key={lot.lotNumber || index} lot={lot} />
                ))
              ) : (
                <Card>
                  <Typography.Text type="secondary">No bids found</Typography.Text>
                </Card>
              )}
            </Space>
          ) : (
            <Table
              columns={bidColumns}
              dataSource={groupedBids}
              rowKey={(r, i) => r._id || i}
              pagination={false}
              locale={{ emptyText: 'No bids found' }}
            />
          )}
        </div>
      )
    },
    {
      key: '1',
      label: `Winning Lots (${winCars.length})`,
      children: (
        <div>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>Currently Winning Lots</Typography.Title>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            These are auctions where you're currently the highest bidder.
          </Typography.Text>
          <Table
            columns={winCarsColumns}
            dataSource={winCars}
            rowKey={(r) => r._id || r.lotNumber}
            pagination={false}
            locale={{
              emptyText: 'No winning lots yet. Winning lots are auctions where you\'re currently the highest bidder.'
            }}
          />
        </div>
      )
    },
    {
      key: '2',
      label: `Purchased Cars (${purchasedCars.length})`,
      children: (
        <div>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>Purchased Cars</Typography.Title>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            These are cars you've actually won. Auctions are complete and payment is required.
          </Typography.Text>
          <Table
            columns={purchasedColumns}
            dataSource={purchasedCars}
            rowKey={(r) => r._id || r.lotNumber}
            pagination={false}
            locale={{
              emptyText: 'No purchased cars yet. Purchased cars are vehicles you\'ve won at auction and need to pay for.'
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
          Back to Subdealers
        </Button>

        <Flex align="center" gap={16} style={{ marginBottom: 16 }}>
          <Avatar size={64} style={{ backgroundColor: '#8b5cf6', fontSize: 24 }}>
            {subdealer.avatar || subdealer.name?.substring(0, 2).toUpperCase() || 'S'}
          </Avatar>
          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>{subdealer.name}</Typography.Title>
            <Flex align="center" gap={8} wrap="wrap" style={{ marginTop: 4 }}>
              <Tag icon={<BankOutlined />} color="blue">{branchName}</Tag>
              <Tag icon={<UserOutlined />} color="purple">{subdealer.managerName}</Tag>
              <Tag icon={<ShopOutlined />} color="cyan">{subdealer.dealerName}</Tag>
              <Typography.Text type="secondary">@{subdealer.user_name}</Typography.Text>
            </Flex>
          </div>
        </Flex>

        <Flex gap={16} style={{ marginBottom: 8 }}>
          <Typography.Text type="secondary">📧 {subdealer.email}</Typography.Text>
          <Typography.Text type="secondary">📱 {subdealer.phone}</Typography.Text>
        </Flex>

        {(subdealer.createdBy || subdealer.createdAt) && (
          <div style={{ marginTop: 16 }}>
            <CreatorInfo createdBy={subdealer.createdBy} createdAt={subdealer.createdAt} />
          </div>
        )}
      </div>

      {/* Calculator Settings Card */}
      <Card style={{ marginBottom: 24 }}>
        <Typography.Title level={5} style={{ marginBottom: 16 }}>Calculator Settings</Typography.Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary">Show Calculator</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Tag color={subdealer.show_calculator !== false ? 'success' : 'default'}>
                {subdealer.show_calculator !== false ? 'Enabled' : 'Disabled'}
              </Tag>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary">Show Discount</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <Tag color={subdealer.show_discount ? 'blue' : 'default'}>
                {subdealer.show_discount ? 'Visible' : 'Hidden'}
              </Tag>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary">Discount %</Typography.Text>
            <div style={{ marginTop: 4, fontWeight: 600 }}>{subdealer.discountPercent || 0}%</div>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsLoading ? (
          <Col span={24}>
            <Flex justify="center" align="center" style={{ padding: 48 }}>
              <Spin size="large" />
              <Typography.Text style={{ marginLeft: 16 }}>Loading statistics...</Typography.Text>
            </Flex>
          </Col>
        ) : (
          stats.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card size="small">
                <Flex align="center" gap={8}>
                  <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                  <div>
                    <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                    <Typography.Text type="secondary">{stat.title}</Typography.Text>
                    {stat.subtitle && (
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                        {stat.subtitle}
                      </Typography.Text>
                    )}
                  </div>
                </Flex>
              </Card>
            </Col>
          ))
        )}
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>
    </div>
  );
};

export default SubdealerDetail;
